import { LibraryItem, ProgramMaterial, XhtmlString } from '@/@types/client-api';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import libraryEndpoints from '../api/client/library';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

// digital-program: digital access granted from program access.
// digital-plusMember: digital access granted from plus membership
// these are not related to STS.
export type EcomBookVariantFormat =
  | 'digital-program'
  | 'digital-plusMember'
  | 'hardcover'
  | 'softcover'
  | 'binder'
  | 'preorder'
  | 'sts'
  | 'sts-renewal';

export type PoecExtensionData = {
  isPoecExtensionVariant?: boolean; // If the variant is POEC OD extension
  isPoecOdVariantEligibleForExtension?: boolean; // If the variant is regular POEC OD and user is eligible to extend (by purchasing extension)
  poecExtensionEligibleItems?: LibraryItem[]; // POEC Items user may be eligible to renew
  poecExtensionTargetItem?: LibraryItem; // The item expected to be renewed by purchasing the extension
};

export type Variant = {
  code?: string;
  displayName?: string;
  thumbnail?: string;
  location?: string;
  address1?: string;
  address2?: string;
  duration?: string;
  progress?: number;
  publicationDate?: string;
  lastUpdatedDate?: string;
  eventStartDate?: string;
  eventEndDate?: string;
  timeZoneIdentifier?: string;
  ecomBoxSessions?: number;
  customDuration?: string;
  price?: string;
  originalPrice?: string;
  itemClass?: number;
  retailPrice?: number;
  discountPrice?: number;
  type?: 'online' | 'in-person' | 'groupcast';
  format?: EcomBookVariantFormat;
  waitlist?: boolean;
  retail?: boolean;
  hasMaterials?: boolean;
  transcriptUrl?: string;
  registered?: boolean; // Live
  isExpired?: boolean;
  sk?: number;
  materials?: ProgramMaterial[];
  purchased?: boolean; // On Demand
  isLive?: boolean;
  hasUpkeep?: boolean; // Publications
  readOnPlusUrl?: string;
  contentLinkUrl?: string;
  isStsRenewalPermitted?: boolean;
  purchasedStsExpirationDate?: string;
  priorityCode?: string; // For STS Publication renewal flow
  notifyForm?: NotifyPanelItems;
  launchUrl?: string;
  isPrintableTreatise?: boolean;
  poecExtensionData?: PoecExtensionData;
};

export type NotifyPanelItems = {
  url?: string;
  buttonLabel?: string;
  messageLabel?: XhtmlString;
  headerLabel?: string;
};

export type CatalogRelations = {
  variants: Array<Variant>;
  expiredPrintVariants?: Array<Variant>;
};

export type PDPLibraryItem = { data?: LibraryItem } & { isFetching?: boolean };
export type PDPMaterials = { data?: ProgramMaterial[] } & { isFetching?: boolean };

export interface PDPSliceSectionType {
  selectedVariant?: Variant;
  catalogRelations?: CatalogRelations;
  hardSelected: boolean;
  loading: boolean;
  materialsLoading?: boolean;
  registered?: boolean;
  registrationModalOpen: boolean;
  loadedComponents: number;
  totalComponents: number;
  hasSegmentsSection?: boolean;
  hasTestimonialsSection?: boolean;
  isStsRenewalSelected?: boolean;
  isFooterInView?: boolean;
}

// box: It is the section in the pdp page, this is the default value
// panel: Right side collapsing panel
export enum SliceSectionName {
  BOX = 'box',
  PANEL = 'panel'
}

export const DEFAULT_SLICE_SECTION_NAME = SliceSectionName.BOX;

export type SliceNameType = SliceSectionName.BOX | SliceSectionName.PANEL;

export interface PDPSliceType {
  sliceName: SliceNameType;
  box: PDPSliceSectionType;
  panel: PDPSliceSectionType;
  library: Record<string, PDPLibraryItem>;
  materials: Record<string, PDPMaterials>;
}

const initialSectionState: PDPSliceSectionType = {
  selectedVariant: {},
  catalogRelations: {
    variants: []
  },
  hardSelected: false,
  loading: false,
  materialsLoading: true,
  registered: false,
  registrationModalOpen: false,
  loadedComponents: 0,
  totalComponents: 0,
  hasSegmentsSection: false,
  hasTestimonialsSection: false,
  isStsRenewalSelected: false,
  isFooterInView: false
};

const initialState: PDPSliceType = {
  sliceName: SliceSectionName.BOX,
  box: initialSectionState,
  panel: initialSectionState,
  library: {},
  materials: {}
};

export const useSliceSectionSelector = <M extends keyof PDPSliceType, T>(
  sliceName: M,
  selector: (slice: PDPSliceType[M]) => T
): T => {
  return useSelector((state: RootState) => {
    const slice = state.pdp[sliceName] as PDPSliceType[M];
    return selector(slice);
  });
};

export const pdpSlice = createSlice({
  name: 'pdp',
  initialState,
  reducers: {
    resetPDPContext: (state, action: PayloadAction<SliceNameType>) => {
      const section: keyof PDPSliceType = action.payload ?? DEFAULT_SLICE_SECTION_NAME;
      state[section].selectedVariant = undefined;
      state[section].catalogRelations = undefined;
      state[section].hardSelected = false;
      state[section].loading = false;
      state[section].materialsLoading = true;
      state[section].registrationModalOpen = false;
      state[section].loadedComponents = 0;
      state[section].totalComponents = 0;
      state[section].hasSegmentsSection = false;
      state[section].isFooterInView = false;
    },

    setSoftSelectedVariant: (
      state,
      action: PayloadAction<{ sliceName: SliceNameType; variant: Variant }>
    ) => {
      const section: keyof PDPSliceType = action.payload.sliceName ?? DEFAULT_SLICE_SECTION_NAME;
      state[section].selectedVariant = action.payload.variant;
      state[section].hardSelected = false;
    },
    setHardSelectedVariant: (
      state,
      action: PayloadAction<{ sliceName: SliceNameType; variant: Variant }>
    ) => {
      const section: keyof PDPSliceType = action.payload.sliceName ?? DEFAULT_SLICE_SECTION_NAME;
      state[section].selectedVariant = action.payload.variant;
      state[section].hardSelected = true;
      state[section].isStsRenewalSelected = action.payload?.variant?.format === 'sts-renewal';
    },
    setCatalogRelations: (
      state,
      action: PayloadAction<{ sliceName: SliceNameType; catalogRelations: CatalogRelations }>
    ) => {
      const section: keyof PDPSliceType = action.payload.sliceName ?? DEFAULT_SLICE_SECTION_NAME;
      state[section].catalogRelations = action.payload.catalogRelations;
    },
    setLoading: (
      state,
      action: PayloadAction<{ sliceName: SliceNameType; isLoading: boolean }>
    ) => {
      const section: keyof PDPSliceType = action.payload.sliceName ?? DEFAULT_SLICE_SECTION_NAME;
      state[section].loading = action.payload.isLoading;
    },
    setOpenRegistrationModal: (
      state,
      action: PayloadAction<{ sliceName: SliceNameType; isRegistrationModalOpen: boolean }>
    ) => {
      const section: keyof PDPSliceType = action.payload.sliceName ?? DEFAULT_SLICE_SECTION_NAME;
      state[section].registrationModalOpen = action.payload.isRegistrationModalOpen;
    },
    setMaterialsLoading: (
      state,
      action: PayloadAction<{ sliceName: SliceNameType; isMaterialsLoading: boolean }>
    ) => {
      const section: keyof PDPSliceType = action.payload.sliceName ?? DEFAULT_SLICE_SECTION_NAME;
      state[section].materialsLoading = action.payload.isMaterialsLoading;
    },
    registerAsyncComponent: (state, action: PayloadAction<SliceNameType>) => {
      const section: keyof PDPSliceType = action.payload ?? DEFAULT_SLICE_SECTION_NAME;
      state[section].loadedComponents += 1;
    },
    markComponentAsLoaded: (state, action: PayloadAction<SliceNameType>) => {
      const section: keyof PDPSliceType = action.payload ?? DEFAULT_SLICE_SECTION_NAME;
      state[section].totalComponents += 1;
    },
    registerSegmentsSection: (state, action: PayloadAction<SliceNameType>) => {
      const section: keyof PDPSliceType = action.payload ?? DEFAULT_SLICE_SECTION_NAME;
      state[section].hasSegmentsSection = true;
    },
    registerTestimonialsSection: (state, action: PayloadAction<SliceNameType>) => {
      const section: keyof PDPSliceType = action.payload ?? DEFAULT_SLICE_SECTION_NAME;
      state[section].hasTestimonialsSection = true;
    },
    unregisterTestimonialsSection: (state, action: PayloadAction<SliceNameType>) => {
      const section: keyof PDPSliceType = action.payload ?? DEFAULT_SLICE_SECTION_NAME;
      state[section].hasTestimonialsSection = false;
    },
    setIsFooterInView: (
      state,
      action: PayloadAction<{ sliceName: SliceNameType; isFooterInView: boolean }>
    ) => {
      const section: keyof PDPSliceType = action.payload.sliceName ?? DEFAULT_SLICE_SECTION_NAME;
      state[section].isFooterInView = action.payload.isFooterInView;
    }
  },
  extraReducers: builder => {
    builder
      .addMatcher(libraryEndpoints.endpoints.getLibraryItemsByPks.matchPending, (state, action) => {
        const pendingItemPks = action.meta.arg.originalArgs;
        pendingItemPks.forEach(itemPk => {
          state.library[itemPk] = { data: undefined, isFetching: true };
        });
      })
      .addMatcher(
        libraryEndpoints.endpoints.getLibraryItemsByPks.matchFulfilled,
        (state, action) => {
          const requestedItemPks = action.meta.arg.originalArgs;

          const parentItems = action.payload ?? [];
          const nestedSegmentItems =
            parentItems?.flatMap(p => (p.segments ?? []).map(s => s)) ?? [];

          const returnedItems = [...parentItems, ...nestedSegmentItems];
          const notReturnedItemPks = requestedItemPks.filter(
            requestedPk => !returnedItems.find(item => item.pk === requestedPk)
          );

          returnedItems.forEach(item => {
            if (item.pk) state.library[item.pk] = { data: item, isFetching: false };
          });
          notReturnedItemPks.forEach(itemPk => {
            // Clear the loading state for items removed from library
            state.library[itemPk] = { data: undefined, isFetching: false };
          });
        }
      )
      .addMatcher(libraryEndpoints.endpoints.getProgramMaterials.matchPending, (state, action) => {
        const itemPk = action.meta.arg.originalArgs;
        state.materials[itemPk] = { data: undefined, isFetching: true };
      })
      .addMatcher(
        libraryEndpoints.endpoints.getProgramMaterials.matchFulfilled,
        (state, action) => {
          const itemPk = action.meta.arg.originalArgs;
          state.materials[itemPk] = { data: action.payload, isFetching: false };
        }
      );
  }
});

export const selectVariant = createAsyncThunk(
  'pdp/setDelayedSelectedVariant',
  async (payload: { sliceName: SliceNameType; variant: Variant }, { dispatch }) => {
    dispatch(pdpSlice.actions.setLoading({ sliceName: payload.sliceName, isLoading: true }));
    dispatch(
      pdpSlice.actions.setHardSelectedVariant({
        sliceName: payload.sliceName,
        variant: payload.variant
      })
    );

    setTimeout(() => {
      dispatch(pdpSlice.actions.setLoading({ sliceName: payload.sliceName, isLoading: false }));
    }, 1200);
  }
);

// Action creators are generated for each case reducer function
export const {
  resetPDPContext,
  setSoftSelectedVariant,
  setHardSelectedVariant,
  setCatalogRelations,
  setOpenRegistrationModal,
  setMaterialsLoading,
  registerAsyncComponent,
  markComponentAsLoaded,
  registerSegmentsSection,
  registerTestimonialsSection,
  unregisterTestimonialsSection,
  setIsFooterInView
} = pdpSlice.actions;

export default pdpSlice.reducer;
